/**
 * Electrical Installation Condition Report (EICR) - Validation & Verdict Engine
 * Uses the C1/C2/C3/FI classification engine to compute overall assessment
 */
import { normalizeCode, computeOverallAssessment } from '../../utils/electrical/eicrClassification.js';
import { ZsCalculator } from '../../utils/electrical/bs7671Tables.js';

const parseCircuitDevice = (circuit) => {
  const type = circuit.protectiveDevice || '';
  const parts = type.split(' ');
  const base = parts[0];
  const variant = parts.slice(1).join(' ');
  return {
    type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
    typeVariant: base === 'BS' ? `BS ${variant}` : variant,
    rating: parseInt(circuit.deviceRating, 10) || 0,
    disconnectionTime: '0.4s'
  };
};

export default {
  validate: (data) => {
    const errors = {};
    const warnings = {};

    // Report details
    if (!data.reportDetails?.reportNumber?.trim()) {
      errors.reportNumber = 'Report number is required';
    }
    if (!data.reportDetails?.reportDate) {
      errors.reportDate = 'Date of report is required';
    }
    if (!data.reportDetails?.purposeOfReport) {
      errors.purposeOfReport = 'Purpose of report is required';
    }

    // Client & property
    if (!data.client?.clientName?.trim()) {
      errors.clientName = 'Client name is required';
    }
    if (!data.property?.propertyAddress?.trim()) {
      errors.propertyAddress = 'Property address is required';
    }

    // Circuits validation with Zs
    if (!data.circuits || data.circuits.length === 0) {
      errors.circuits = 'At least one circuit must be recorded';
    } else {
      data.circuits.forEach((circuit, index) => {
        const prefix = `circuit_${index}`;
        if (!circuit.location?.trim()) {
          errors[`${prefix}_location`] = 'Circuit location is required';
        }

        // Zs compliance
        if (circuit.protectiveDevice && circuit.deviceRating) {
          const device = parseCircuitDevice(circuit);
          const maxZs = ZsCalculator.getMaxZs(device);
          if (maxZs !== null && circuit.maxZs === '') {
            circuit.maxZs = maxZs;
          }
          if (circuit.measuredZs && maxZs && parseFloat(circuit.measuredZs) > maxZs) {
            warnings[`${prefix}_measuredZs`] =
              `Measured Zs (${circuit.measuredZs} Ω) exceeds max permitted (${maxZs.toFixed(2)} Ω).`;
          }
        }

        // Insulation resistance
        if (circuit.insulationResistance && parseFloat(circuit.insulationResistance) < 1.0) {
          warnings[`${prefix}_insulationResistance`] =
            'Insulation resistance is below minimum 1.0 MΩ.';
        }
      });
    }

    // Observations validation
    data.observations?.forEach((obs, index) => {
      const prefix = `observation_${index}`;
      if (!obs.observationCode) {
        errors[`${prefix}_observationCode`] = 'Observation class is required';
      }
      if (!obs.observationDetails?.trim()) {
        errors[`${prefix}_observationDetails`] = 'Observation details are required';
      }
    });

    // Compute overall assessment from observation codes
    const codes = (data.observations || [])
      .map(o => o.observationCode)
      .filter(Boolean);
    const outcome = computeOverallAssessment(codes);
    data.overallAssessment = outcome.overallAssessment;

    // Declaration
    if (!data.declaration?.engineerName?.trim()) {
      errors.engineerName = 'Engineer name is required';
    }
    if (!data.declaration?.engineerCardNumber?.trim()) {
      errors.engineerCardNumber = 'Engineer card number is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings,
      overallAssessment: outcome.overallAssessment,
      forceReason: outcome.forceReason
    };
  },

  /**
   * Compute overall EICR outcome from observations
   * Exported for external use (one-tap remedial quote, etc.)
   */
  computeEICROutcome: (observations) => {
    const codes = (observations || []).map(o => o.observationCode).filter(Boolean);
    return computeOverallAssessment(codes);
  }
};